import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { styled } from 'styled-components';
import { useRef } from 'react';
import { isNonEmptyString, isNotNil } from '@wistia/type-guards';
import { Stack } from '../Stack';
import { Heading } from '../Heading';
import { Text } from '../Text';

export type FormGroupProps = Omit<ComponentPropsWithoutRef<'fieldset'>, 'onChange'> & {
  children: ReactNode;
  /**
   * The label for the form group
   */
  label?: ReactNode;
  /**
   * The description for the form group
   */
  description?: ReactNode;
};

const StyledFieldset = styled.fieldset`
  padding: 0;
  margin: 0;
  border: 0;
`;

const StyledLegend = styled.legend`
  padding-inline: 0;
  display: flex;
  flex-direction: column;
  gap: var(--wui-space-01);
  margin-bottom: var(--wui-space-02);
`;

/**
 * For grouping like form elements together.
 */
export const FormGroup = ({
  children,
  label,
  description,
  ...props
}: FormGroupProps): JSX.Element => {
  const ref = useRef<HTMLFieldSetElement | null>(null);
  const hasLabel = isNotNil(label) && isNonEmptyString(label);
  const hasDescription = isNotNil(description) && isNonEmptyString(description);
  return (
    <Stack
      {...props}
      ref={ref}
      renderAs={StyledFieldset}
    >
      {hasLabel || hasDescription ? (
        <Heading
          renderAs={StyledLegend}
          variant="heading5"
        >
          {hasLabel ? label : null}
          {hasDescription ? (
            <Text
              data-wui-form-group-description={true}
              prominence="secondary"
              renderAs="span"
              variant="body4"
            >
              {description}
            </Text>
          ) : null}
        </Heading>
      ) : null}

      {children}
    </Stack>
  );
};

FormGroup.displayName = 'FormGroup';
